import { useCallback, useEffect, useState } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  Badge,
  Button,
  ButtonGroup,
  InlineStack,
  Box,
  Tabs,
  Banner,
  EmptyState,
  Spinner,
  Thumbnail,
  Link,
  Modal,
} from '@shopify/polaris';
import {
  listReviews,
  approveReview as apiApprove,
  rejectReview as apiReject,
  deleteReview as apiDelete,
  type AdminReview,
  type AdminReviewsResponse,
} from './api';

type Status = 'pending' | 'approved' | 'rejected';

function relativeTime(iso: string): string {
  const ts = new Date(iso).getTime();
  const diffMs = Date.now() - ts;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;
  return iso.slice(0, 10);
}

function renderStars(rating: number): string {
  return '★'.repeat(rating) + '☆'.repeat(5 - rating);
}

interface ReviewCardProps {
  review: AdminReview;
  onApprove: (id: string) => Promise<void>;
  onReject: (id: string) => Promise<void>;
  onDelete: (id: string) => void;
  busy: boolean;
}

function ReviewCard({ review, onApprove, onReject, onDelete, busy }: ReviewCardProps): JSX.Element {
  const productNumeric = review.product_id.split('/').pop() ?? review.product_id;
  const productLabel = review.product_title ?? `product #${productNumeric}`;

  return (
    <Card>
      <BlockStack gap="300">
        <InlineStack gap="200" align="space-between" blockAlign="start">
          <BlockStack gap="100">
            <InlineStack gap="200" blockAlign="center">
              <Text as="span" variant="headingMd" fontWeight="bold">
                {renderStars(review.rating)}
              </Text>
              {review.title && (
                <Text as="h3" variant="headingMd">
                  {review.title}
                </Text>
              )}
              {review.verified_purchase && (
                <Badge tone="success">Verified buyer</Badge>
              )}
            </InlineStack>
            <Text as="span" variant="bodySm" tone="subdued">
              {review.display_name ?? 'Anonymous'} · {relativeTime(review.created_at)} ·{' '}
              {review.product_url ? (
                <Link url={review.product_url} target="_blank" removeUnderline>
                  {productLabel}
                </Link>
              ) : (
                productLabel
              )}
            </Text>
          </BlockStack>
          <ButtonGroup>
            {review.status === 'pending' && (
              <>
                <Button
                  variant="primary"
                  tone="success"
                  disabled={busy}
                  onClick={() => onApprove(review.id)}
                >
                  Approve
                </Button>
                <Button
                  tone="critical"
                  disabled={busy}
                  onClick={() => onReject(review.id)}
                >
                  Reject
                </Button>
              </>
            )}
            {review.status === 'approved' && <Badge tone="success">Approved</Badge>}
            {review.status === 'rejected' && <Badge tone="critical">Rejected</Badge>}
            <Button
              variant="plain"
              tone="critical"
              disabled={busy}
              onClick={() => onDelete(review.id)}
            >
              Delete
            </Button>
          </ButtonGroup>
        </InlineStack>

        <Text as="p" variant="bodyMd">
          {review.body}
        </Text>

        {review.photos.length > 0 && (
          <InlineStack gap="200">
            {review.photos.map((p) => (
              <Thumbnail key={p.id} source={p.url} alt="Review photo" size="medium" />
            ))}
          </InlineStack>
        )}

        {review.moderation_note && (
          <Box paddingBlockStart="200">
            <Text as="span" variant="bodySm" tone="subdued">
              Moderation note: {review.moderation_note}
            </Text>
          </Box>
        )}
      </BlockStack>
    </Card>
  );
}

interface Props {
  standalone: boolean;
}

export function ModerationView({ standalone }: Props): JSX.Element {
  const [tab, setTab] = useState<Status>('pending');
  const [data, setData] = useState<AdminReviewsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  const load = useCallback(async (status: Status) => {
    setLoading(true);
    setError(null);
    try {
      const res = await listReviews(status);
      setData(res);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!standalone) void load(tab);
  }, [tab, standalone, load]);

  const handleApprove = useCallback(
    async (id: string) => {
      setBusyId(id);
      try {
        await apiApprove(id);
        await load(tab);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setBusyId(null);
      }
    },
    [tab, load],
  );

  const handleReject = useCallback(
    async (id: string) => {
      setBusyId(id);
      try {
        await apiReject(id);
        await load(tab);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setBusyId(null);
      }
    },
    [tab, load],
  );

  const handleDelete = useCallback(
    async () => {
      if (!pendingDeleteId) return;
      const id = pendingDeleteId;
      setBusyId(id);
      setPendingDeleteId(null);
      try {
        await apiDelete(id);
        await load(tab);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setBusyId(null);
      }
    },
    [pendingDeleteId, tab, load],
  );

  const counts = data?.counts ?? { pending: 0, approved: 0, rejected: 0 };

  const tabs = [
    { id: 'pending', content: `Pending (${counts.pending})` },
    { id: 'approved', content: `Approved (${counts.approved})` },
    { id: 'rejected', content: `Rejected (${counts.rejected})` },
  ];

  const tabIndex = tab === 'pending' ? 0 : tab === 'approved' ? 1 : 2;

  return (
    <Page title="Customer review moderation">
      {standalone && (
        <Layout>
          <Layout.Section>
            <Banner tone="warning" title="Running outside Shopify Admin">
              <p>
                Open this app from Shopify Admin → Apps → HerbanVault Staff to load
                reviews. Direct browser access can't authenticate with App Bridge.
              </p>
            </Banner>
          </Layout.Section>
        </Layout>
      )}

      {!standalone && (
        <BlockStack gap="400">
          <Card padding="0">
            <Tabs
              tabs={tabs}
              selected={tabIndex}
              onSelect={(i) => setTab(i === 0 ? 'pending' : i === 1 ? 'approved' : 'rejected')}
              fitted
            />
          </Card>

          {error && (
            <Banner tone="critical" title="Error loading reviews">
              <p>{error}</p>
              <Button onClick={() => load(tab)}>Retry</Button>
            </Banner>
          )}

          {loading && !data && (
            <Box padding="800">
              <InlineStack align="center">
                <Spinner accessibilityLabel="Loading reviews" />
              </InlineStack>
            </Box>
          )}

          {data && data.reviews.length === 0 && (
            <Card>
              <EmptyState heading={`No ${tab} reviews`} image="">
                <p>
                  {tab === 'pending'
                    ? 'New customer reviews will appear here for moderation.'
                    : tab === 'approved'
                      ? 'Approved reviews are live on the storefront.'
                      : 'Rejected reviews are hidden permanently.'}
                </p>
              </EmptyState>
            </Card>
          )}

          {data && data.reviews.length > 0 && (
            <BlockStack gap="300">
              {data.reviews.map((r) => (
                <ReviewCard
                  key={r.id}
                  review={r}
                  onApprove={handleApprove}
                  onReject={handleReject}
                  onDelete={(id) => setPendingDeleteId(id)}
                  busy={busyId === r.id}
                />
              ))}
            </BlockStack>
          )}

          <Modal
            open={pendingDeleteId !== null}
            onClose={() => setPendingDeleteId(null)}
            title="Delete this review?"
            primaryAction={{
              content: 'Delete permanently',
              destructive: true,
              onAction: handleDelete,
            }}
            secondaryActions={[
              { content: 'Cancel', onAction: () => setPendingDeleteId(null) },
            ]}
          >
            <Modal.Section>
              <Text as="p" variant="bodyMd">
                The review and all its photos will be permanently removed. No audit trail
                is kept. Use Reject instead if you want to hide the review but preserve a
                record.
              </Text>
            </Modal.Section>
          </Modal>
        </BlockStack>
      )}
    </Page>
  );
}
